import { RefObject, useCallback, useRef } from 'react';
import {
  TRUNCATION_TOOLTIP_ATTR_NAME,
  UPDATE_TOOLTIP_EVENT_TYPE,
} from '../../../hooks/useTruncationTooltip';

export interface KeyboardTooltipConfig {
  /**
   * Customize the keyboard tooltip. If not specified, the "title" attribute will be used
   */
  tooltip?: string;
  /**
   * If set to true, all the children elements' titles attributes will be concatenated to form the keyboard tooltip
   */
  traverseChildren?: boolean;
  /**
   * Sepcify which element the focused element should be getting "title" attribute from. By default,
   * the start element is the focused element.
   * For example, when the focused element does not have a "title" attribute but its parent element does,
   * you might want to set startElementRef to be its parent element.
   */
  startElementRef?: RefObject<HTMLElement>;
  /**
   * An array of DOM query selectors. All the "title" attributes of the matching elements will be concatenated
   * to form the keyboard tooltip. Each selector can be either a class selector (starting with ".") or an id selector
   * (starting with "#"). For example, [".myClass", "#id"]
   * For class selectors, the query only traverse the elements under startElement to find the matching elements
   * For id selectors, the query traverse the whole DOM to find the matching elements
   */
  selectors?: ReadonlyArray<string>;
}

const keyboardTooltipConfigMap = new Map<HTMLElement, KeyboardTooltipConfig>();

export const useKeyboardTooltip = (config: KeyboardTooltipConfig) => {
  const prevElementRef = useRef<HTMLElement | null>(null);
  const setRef = useCallback(
    (element: HTMLElement | null) => {
      if (element === null) {
        if (prevElementRef.current) {
          keyboardTooltipConfigMap.delete(prevElementRef.current);
        }
      } else {
        keyboardTooltipConfigMap.set(element, config);
      }

      if (element && element === document.activeElement) {
        // If the element has the focus, update a dummy attribute to trigger the MutationObserver
        // so that the keyboard tooltip is updated
        element.setAttribute('data-keyboard-tooltip', 'dummyData');
        element.removeAttribute('data-keyboard-tooltip');
      }

      prevElementRef.current = element;
    },
    [config]
  );
  return setRef;
};

export const getKeyboardTooltip = (
  element: HTMLElement | null = document.activeElement as HTMLElement | null
): string => {
  const keyboardTooltipConfig = element ? keyboardTooltipConfigMap.get(element) : undefined;
  const tooltips = [];

  // When startElementRef is provided, use it even when it points to `null`
  const startElement =
    keyboardTooltipConfig?.startElementRef === undefined
      ? element
      : keyboardTooltipConfig.startElementRef.current;

  if (keyboardTooltipConfig?.tooltip) {
    tooltips.push(keyboardTooltipConfig.tooltip);
  } else if (startElement?.getAttribute('title')) {
    tooltips.push(startElement.getAttribute('title'));
  }

  const addTooltips = (elements: NodeListOf<any> | undefined) => {
    for (const element of elements?.values?.() ?? []) {
      if (element.getAttribute(TRUNCATION_TOOLTIP_ATTR_NAME)) {
        // let useTruncationTooltip update the title first
        element.dispatchEvent(new Event(UPDATE_TOOLTIP_EVENT_TYPE, { bubbles: false }));
      }
      if (element.getAttribute('title')) {
        tooltips.push(element.getAttribute('title'));
      }
    }
  };

  if (keyboardTooltipConfig?.traverseChildren) {
    addTooltips(
      startElement?.querySelectorAll(`[title]:not([title='']),[${TRUNCATION_TOOLTIP_ATTR_NAME}]`)
    );
  }

  for (const selector of keyboardTooltipConfig?.selectors ?? []) {
    addTooltips((selector.startsWith('.') ? startElement : document)?.querySelectorAll(selector));
  }
  return tooltips.join('\n\n');
};
